import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Package, Truck } from 'lucide-react';
import { Code } from '@/ui/Badge';
import { Button, ButtonGroup } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, Textarea } from '@/ui/Field';
import { SubNav, SubNavItem } from '@/ui/Filters';
import {
  Detail,
  DetailList,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
} from '@/ui/Panel';
import { useShortcutHint } from '@/app/StatusLine';
import { useAtLeast } from '@/lib/auth';
import { api, errorMessage } from '@/lib/api/client';
import { dateTime, humanize, money, phone, qty, relativeTime } from '@/lib/format';
import type { OrderDetail } from '@/lib/api/types';
import {
  Banner,
  CardAttemptBanner,
  DeliveryBadge,
  LineStatusBadge,
  OrderStatusBadge,
  StatusTimeline,
} from '@/features/orders/chrome';
import {
  customerLabel,
  fetchOrder,
  orderUrgency,
  ordersKey,
  outstandingSubstitution,
  pickProgress,
} from '@/features/orders/lib';
import { DeliveryPanel } from '@/features/orders/DeliveryPanel';
import { PackingPanel } from '@/features/orders/PackingPanel';
import {
  MessagesPanel,
  SubstitutionResponseBanner,
} from '@/features/orders/MessagesPanel';
import { useCustomerMessaging } from '@/features/orders/useCustomerMessaging';
import { useOrderWrite } from '@/features/orders/useOrderWrite';

/* The order workflow hub.
   Every affordance on this screen is a function of the order's current state —
   nothing is rendered disabled "for later". `POST /orders/:id/complete` is
   deliberately absent: it needs a real transaction and register, and belongs to
   the register, not the console. So is `POST /orders/:id/auto-settle` ("Charge
   card & complete", TASK-0226): the server has no payment terminal it can reach,
   so the charge could only wedge an attempt and block the order. A picked order
   is paid at a register. (The copy says only "Take payment at a register." and
   never names Ctrl+O or tells staff to load the order: until server tax rates
   are set and order completion is durable, customer orders are rung as normal
   sales.) */

/** The register's checkout lock is server-side and times out after 10 minutes. */
const CHECKOUT_LOCK_MINUTES = 10;

type View = 'lines' | 'packing' | 'messages';

export const Route = createFileRoute('/_app/orders_/$id')({
  staticData: { crumb: 'Order' },
  validateSearch: (s: Record<string, unknown>): { view?: View } => ({
    view: s.view === 'packing' || s.view === 'messages' ? s.view : 'lines',
  }),
  component: OrderDetailScreen,
});

function OrderDetailScreen() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const view: View = search.view ?? 'lines';
  const orderId = Number(id);
  const isManager = useAtLeast('manager');

  useShortcutHint([{ keys: 'Esc', label: 'Back to orders' }]);

  // Escape returns to the queue — unless a dialog owns it, in which case the
  // dialog closes and the order stays put.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (document.querySelector('[role="dialog"]')) return;
      void navigate({ to: '/orders' });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  const detail = useQuery({
    queryKey: ordersKey.detail(orderId),
    queryFn: () => fetchOrder(orderId),
    enabled: Number.isFinite(orderId) && orderId > 0,
    staleTime: 5_000,
  });

  if (detail.isLoading) {
    return (
      <Page>
        <PageHeader eyebrow="Orders" title="Loading order…" />
        <PageBody>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </PageBody>
      </Page>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <Page>
        <PageHeader eyebrow="Orders" title={`Order ${id}`} />
        <PageBody>
          <ErrorState
            title="Could not load this order"
            message={errorMessage(detail.error)}
            onRetry={() => void detail.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  const order = detail.data;
  const progress = pickProgress(order.items);
  const urgency = orderUrgency(order);
  const pending = outstandingSubstitution(order.notifications);

  return (
    <Page>
      <PageHeader
        eyebrow="Orders"
        title={
          <span className="flex items-center gap-2.5">
            <span className="font-mono">{order.orderNumber}</span>
            <OrderStatusBadge status={order.status} dot />
            {order.fulfillmentType === 'delivery' ? (
              <DeliveryBadge status={order.deliveryStatus} />
            ) : null}
          </span>
        }
        subtitle={
          <span className="flex items-center gap-2">
            <span>{customerLabel(order)}</span>
            {order.customerPhone ? <span>· {phone(order.customerPhone)}</span> : null}
            <span>· {humanize(order.fulfillmentType)}</span>
            <span>
              ·{' '}
              {order.requestedFor
                ? `due ${dateTime(order.requestedFor)}`
                : 'no requested time'}
            </span>
            <span>
              · {progress.resolved}/{progress.total} lines resolved
            </span>
          </span>
        }
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<ArrowLeft className="size-3.5" />}
              onClick={() => void navigate({ to: '/orders' })}
            >
              Orders
            </Button>
            <LifecycleActions order={order} />
          </>
        }
        tabs={
          <SubNav>
            <SubNavItem
              active={view === 'lines'}
              onClick={() => void navigate({ search: { view: 'lines' } })}
              count={order.items.length}
            >
              Lines
            </SubNavItem>
            <SubNavItem
              active={view === 'packing'}
              onClick={() => void navigate({ search: { view: 'packing' } })}
              count={order.containers.length}
            >
              Packing
            </SubNavItem>
            <SubNavItem
              active={view === 'messages'}
              onClick={() => void navigate({ search: { view: 'messages' } })}
              count={order.notifications.length}
            >
              Messages
            </SubNavItem>
          </SubNav>
        }
      />

      <PageBody>
        <div className="space-y-3">
          <CardAttemptBanner
            orderId={order.id}
            attempt={order.cardChargeAttempt}
            canResolve={isManager}
          />

          {pending ? (
            <SubstitutionResponseBanner order={order} notificationId={pending.id} />
          ) : null}

          {urgency ? (
            <Banner
              tone={urgency === 'overdue' ? 'negative' : 'warn'}
              title={
                urgency === 'overdue'
                  ? `Past its requested time — ${dateTime(order.requestedFor)}`
                  : `Due ${relativeTime(order.requestedFor)} — ${dateTime(order.requestedFor)}`
              }
            />
          ) : null}

          {order.status === 'cancelled' ? (
            <Banner tone="neutral" title="This order was cancelled">
              {order.cancelledReason || 'No reason was recorded.'}
            </Banner>
          ) : null}

          {order.deliveryStatus === 'delivery_failed' ? (
            <Banner tone="negative" title="Delivery failed" icon={<Truck />}>
              {order.deliveryFailureReason || 'No reason was recorded.'} Assigning a
              driver and dispatching again will clear it.
            </Banner>
          ) : null}

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="min-w-0 space-y-3">
              {view === 'lines' ? <LinesPanel order={order} /> : null}
              {view === 'packing' ? <PackingPanel order={order} /> : null}
              {view === 'messages' ? <MessagesPanel order={order} /> : null}
            </div>

            <div className="min-w-0 space-y-3">
              <SummaryPanel order={order} />
              {order.fulfillmentType === 'delivery' ? (
                <DeliveryPanel order={order} />
              ) : null}
              <Panel title="History" bleed>
                <StatusTimeline entries={order.statusLog} />
              </Panel>
            </div>
          </div>
        </div>
      </PageBody>
    </Page>
  );
}

/* ── Lifecycle actions ────────────────────────────────────────────────────── */

function LifecycleActions({ order }: { order: OrderDetail }) {
  const navigate = Route.useNavigate();
  const write = useOrderWrite(order.id);
  const { canReachCustomer } = useCustomerMessaging();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [reason, setReason] = useState('');

  const lockAgeMs = order.checkoutStartedAt
    ? Date.now() - Date.parse(order.checkoutStartedAt)
    : Number.POSITIVE_INFINITY;
  const checkoutLocked =
    order.checkoutRegisterId !== null &&
    order.checkoutStartedAt !== null &&
    lockAgeMs <= CHECKOUT_LOCK_MINUTES * 60_000;

  const submit = useMutation({
    mutationFn: () => api.post<OrderDetail>(`/orders/${order.id}/submit`),
    onSuccess: (data) => {
      write.applyDetail(data);
      toast.success(`${order.orderNumber} submitted`, {
        description: 'Stock is reserved and it is in the pick queue.',
      });
    },
    onError: (e) => toast.error('Could not submit', { description: errorMessage(e) }),
  });

  const claim = useMutation({
    mutationFn: () => api.post<OrderDetail>(`/orders/${order.id}/claim`),
    onSuccess: (data) => {
      write.applyDetail(data);
      void navigate({ to: '/orders/$id/pick', params: { id: String(order.id) } });
    },
    onError: (e) => toast.error('Could not claim', { description: errorMessage(e) }),
  });

  const abandon = useMutation({
    mutationFn: () => api.post<OrderDetail>(`/orders/${order.id}/abandon`),
    onSuccess: (data) => {
      write.applyDetail(data);
      setAbandonOpen(false);
      toast.success('Pick released', {
        description: 'The order is back in the open queue and every line was reset.',
      });
    },
    onError: (e) => toast.error('Could not abandon', { description: errorMessage(e) }),
  });

  const cancel = useMutation({
    mutationFn: () =>
      api.post<OrderDetail>(`/orders/${order.id}/cancel`, {
        reason: reason.trim() || undefined,
      }),
    onSuccess: (data) => {
      write.applyDetail(data);
      setCancelOpen(false);
      setReason('');
      toast.success(`${order.orderNumber} cancelled`);
    },
    onError: (e) => toast.error('Could not cancel', { description: errorMessage(e) }),
  });

  return (
    <>
      <ButtonGroup>
        {order.status === 'draft' && order.items.length > 0 ? (
          <Button
            variant="primary"
            size="sm"
            loading={submit.isPending}
            onClick={() => submit.mutate()}
          >
            Submit order
          </Button>
        ) : null}

        {order.status === 'submitted' ? (
          <Button
            variant="primary"
            size="sm"
            icon={<Package className="size-3.5" />}
            loading={claim.isPending}
            onClick={() => claim.mutate()}
          >
            Claim &amp; pick
          </Button>
        ) : null}

        {order.status === 'picking' ? (
          <>
            <Button
              variant="primary"
              size="sm"
              icon={<Package className="size-3.5" />}
              onClick={() =>
                void navigate({
                  to: '/orders/$id/pick',
                  params: { id: String(order.id) },
                })
              }
            >
              Continue picking
            </Button>
            <Button size="sm" onClick={() => setAbandonOpen(true)}>
              Release
            </Button>
          </>
        ) : null}

        {['draft', 'submitted', 'picking'].includes(order.status) ? (
          <Button variant="danger" size="sm" onClick={() => setCancelOpen(true)}>
            Cancel
          </Button>
        ) : null}
      </ButtonGroup>

      {order.status === 'picked' && checkoutLocked ? (
        <span className="text-2xs text-warn">
          Locked by register #{order.checkoutRegisterId} for checkout
        </span>
      ) : null}

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={`Cancel ${order.orderNumber}?`}
        description={
          canReachCustomer
            ? 'Reserved stock is released and the customer is messaged if a phone or email is on file.'
            : 'Reserved stock is released. The customer is not messaged — let them know yourself.'
        }
        confirmLabel="Cancel order"
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
        consequences={[
          `Releases the stock reserved for ${order.items.length} ${order.items.length === 1 ? 'line' : 'lines'}`,
          canReachCustomer
            ? 'Messages the customer that the order was cancelled'
            : 'Logs an order-cancelled note on the order — the customer is not messaged',
          'The order cannot be re-opened — a new order would have to be built',
        ]}
      >
        <Field label="Reason" hint="Recorded on the order and in the status log.">
          <Textarea
            value={reason}
            rows={2}
            placeholder="Customer changed their mind…"
            onChange={(e) => setReason(e.currentTarget.value)}
          />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={abandonOpen}
        onOpenChange={setAbandonOpen}
        title="Release this pick?"
        description="The order goes back to the open queue for someone else."
        confirmLabel="Release the claim"
        loading={abandon.isPending}
        onConfirm={() => abandon.mutate()}
        consequences={[
          'Every line is reset to pending — picked quantities, weights and substitutions are erased',
          'Reservations are rebuilt from the originally ordered quantities',
        ]}
      />
    </>
  );
}

/* ── Lines ────────────────────────────────────────────────────────────────── */

function LinesPanel({ order }: { order: OrderDetail }) {
  const progress = pickProgress(order.items);
  const estimated = order.items.reduce((s, i) => s + i.estimatedExtPriceCents, 0);
  const actual = order.items.reduce((s, i) => s + (i.actualExtPriceCents ?? 0), 0);

  return (
    <Panel
      title="Lines"
      description={`${progress.resolved} of ${progress.total} resolved`}
      bleed
      footer={
        <div className="flex items-center gap-4">
          <span>
            Estimated{' '}
            <span className="text-ink font-mono tabular-nums">{money(estimated)}</span>
          </span>
          {order.actualTotalCents !== null ? (
            <span>
              Picked{' '}
              <span className="text-ink font-mono tabular-nums">{money(actual)}</span>
            </span>
          ) : null}
          <span className="ml-auto">
            Order total{' '}
            <span className="text-ink font-mono tabular-nums">
              {money(order.actualTotalCents ?? order.estimatedTotalCents)}
            </span>
          </span>
        </div>
      }
    >
      <div className="divide-line-soft divide-y">
        <div className="bg-surface-sunken text-2xs text-ink-faint grid grid-cols-[minmax(0,1fr)_5rem_5rem_6rem_6.5rem] items-center gap-3 px-3 py-1 tracking-wider uppercase">
          <span>Item</span>
          <span className="text-right">Ordered</span>
          <span className="text-right">Picked</span>
          <span>Status</span>
          <span className="text-right">Amount</span>
        </div>
        {order.items.map((item) => (
          <div
            key={item.id}
            className="grid grid-cols-[minmax(0,1fr)_5rem_5rem_6rem_6.5rem] items-center gap-3 px-3 py-1.5"
          >
            <div className="min-w-0">
              <div className="text-ink truncate text-xs">{item.productNameSnapshot}</div>
              <div className="text-2xs text-ink-faint mt-0.5 flex items-center gap-2">
                {item.zoneName ? <Code>{item.zoneName}</Code> : null}
                {item.soldByWeight ? <span>by weight</span> : null}
                {item.actualWeightLbs ? (
                  <span className="font-mono">{qty(item.actualWeightLbs)} lb</span>
                ) : null}
                {item.substitutedProductName ? (
                  <span className="truncate">→ {item.substitutedProductName}</span>
                ) : null}
                {item.substitutionPref ? (
                  <span>{humanize(item.substitutionPref)}</span>
                ) : null}
              </div>
              {item.pickNote ? (
                <div className="text-2xs text-ink-muted mt-0.5 truncate">
                  {item.pickNote}
                </div>
              ) : null}
            </div>
            <div className="text-ink-muted text-right font-mono text-xs tabular-nums">
              {qty(item.orderedQty)}
            </div>
            <div className="text-ink text-right font-mono text-xs tabular-nums">
              {item.pickedQty === null ? '—' : qty(item.pickedQty)}
            </div>
            <div>
              <LineStatusBadge status={item.lineStatus} />
            </div>
            <div className="text-ink text-right font-mono text-xs tabular-nums">
              {money(item.actualExtPriceCents ?? item.estimatedExtPriceCents)}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ── Summary ──────────────────────────────────────────────────────────────── */

function SummaryPanel({ order }: { order: OrderDetail }) {
  return (
    <>
      <Panel title="Order">
        <DetailList columns={2}>
          <Detail label="Source">{humanize(order.source)}</Detail>
          <Detail label="Fulfilment">{humanize(order.fulfillmentType)}</Detail>
          <Detail label="Placed">{dateTime(order.createdAt)}</Detail>
          <Detail label="Requested for">
            {order.requestedFor ? dateTime(order.requestedFor) : 'No time set'}
          </Detail>
          <Detail label="Substitutions">
            {order.substitutionPref
              ? humanize(order.substitutionPref)
              : 'Customer default'}
          </Detail>
          <Detail label="Campaign">
            {order.campaignId ? (order.campaignName ?? `#${order.campaignId}`) : 'None'}
          </Detail>
        </DetailList>
        {order.note ? (
          <div className="border-line bg-surface-sunken text-ink mt-2 rounded border p-2 text-xs leading-relaxed">
            {order.note}
          </div>
        ) : null}
      </Panel>

      <Panel title="Payment">
        <DetailList columns={2}>
          <Detail label="Method">{humanize(order.paymentMethod)}</Detail>
          <Detail label="Status">
            {order.paymentStatus ? humanize(order.paymentStatus) : 'Not charged'}
          </Detail>
          <Detail label="Estimated" mono>
            {money(order.estimatedTotalCents)}
          </Detail>
          <Detail label="Picked total" mono>
            {order.actualTotalCents === null ? '—' : money(order.actualTotalCents)}
          </Detail>
          <Detail label="Delivery fee" mono>
            {money(order.deliveryFeeCents)}
          </Detail>
          <Detail label="Tip" mono>
            {money(order.tipCents)}
          </Detail>
          {order.paymentRefNumber ? (
            <Detail label="Gateway ref" mono>
              {order.paymentRefNumber}
            </Detail>
          ) : null}
          {order.transactionId ? (
            <Detail label="Transaction" mono>
              <Code copyable>{order.transactionId}</Code>
            </Detail>
          ) : null}
        </DetailList>
        {order.paymentError ? (
          <div className="border-negative/30 bg-negative-muted text-2xs text-ink mt-2 rounded border p-2 font-mono">
            {order.paymentError}
          </div>
        ) : null}
      </Panel>
    </>
  );
}
